import React from "react";
import { BACKEND_URI } from "../../constants";
import Fetch from "../../api/Fetch";
import { Event } from "../../model/Types";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import EventForm from "../../components/event/EventForm";
import { DateTime } from "luxon";
import Toaster from "../../common/Toaster";
import { Link } from "react-router-dom";

const AdminEvents = () => {
    const [events, setEvents] = React.useState<Event[]>([]);
    const [showEventDialog, setShowEventDialog] = React.useState<boolean>(false);
    const [currentEvent, setCurrentEvent] = React.useState<Event | undefined>(undefined);
    const [showCurrentOnly, setShowCurrentOnly] = React.useState<boolean>(true);

    const cb = React.useCallback(async () => {
        try {
            const body = await Fetch<Event[]>(`${BACKEND_URI}/admin/event`, {
                includeAuth: true,
            });
            setEvents(body);
        } catch (err) {
            console.log(err);
        }
    }, []);

    async function onSubmit(request: any) {
        try {
            await Fetch(`${BACKEND_URI}/admin/event`, {
                method: "POST",
                body: request,
                includeAuth: true,
            });
            handleClose();
            Toaster.success(`Event ${request.id ? "updated" : "created"} successfully.`);
            cb();
        } catch (err: any) {
            console.log(err);
            Toaster.error(err.message || "An error occurred while adding event.");
        }
    }

    function handleClose() {
        setShowEventDialog(false);
        setCurrentEvent(undefined);
    }

    React.useEffect(() => {
        cb();
    }, [cb]);

    const isEventCurrentOrUpcoming = (event: Event) => {
        const now = DateTime.now();
        const eventEnd = DateTime.fromISO(event.eventEnd);
        return eventEnd >= now;
    };

    const filteredEvents = showCurrentOnly ? events.filter(isEventCurrentOrUpcoming) : events;

    return (
        <Box>
            <Card>
                <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Typography variant="h5">Events</Typography>
                            <Chip label={`${filteredEvents.length} of ${events.length}`} size="small" color="primary" variant="outlined" />
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <FormControlLabel
                                control={<Switch checked={showCurrentOnly} onChange={(e) => setShowCurrentOnly(e.target.checked)} color="primary" />}
                                label="Current & Upcoming Only"
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    setShowEventDialog(true);
                                }}
                            >
                                Add Event
                            </Button>
                        </Box>
                    </Box>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Event</TableCell>
                                <TableCell>Start Date</TableCell>
                                <TableCell>End Date</TableCell>
                                <TableCell>Time Zone</TableCell>
                                <TableCell>EMS Client ID</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredEvents
                                .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                                .map((event) => {
                                    const isUpcoming = isEventCurrentOrUpcoming(event);
                                    const isPast = !isUpcoming;

                                    return (
                                        <TableRow
                                            key={event.id}
                                            sx={{
                                                opacity: isPast ? 0.6 : 1,
                                                backgroundColor: isPast ? "action.disabledBackground" : "inherit",
                                            }}
                                        >
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    {event.name}
                                                    {isPast && <Chip label="Past" size="small" color="default" variant="outlined" />}
                                                    {isUpcoming && <Chip label="Active" size="small" color="success" variant="filled" />}
                                                </Box>
                                            </TableCell>
                                            <TableCell>{DateTime.fromISO(event.eventStart).toFormat("MMM dd, yyyy")}</TableCell>
                                            <TableCell>{DateTime.fromISO(event.eventEnd).toFormat("MMM dd, yyyy")}</TableCell>
                                            <TableCell>{event.timezone}</TableCell>
                                            <TableCell>{event.clientId}</TableCell>
                                            <TableCell>
                                                <Box>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() => {
                                                            setCurrentEvent(event);
                                                            setShowEventDialog(true);
                                                        }}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Link to={`/admin/event/${event.id}`} style={{ marginLeft: 10 }}>
                                                        <Button variant="contained" color="secondary">
                                                            View
                                                        </Button>
                                                    </Link>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
            <Dialog open={showEventDialog}>
                <DialogTitle>Add Event</DialogTitle>
                <DialogContent>
                    <EventForm
                        event={currentEvent}
                        onSubmit={onSubmit}
                        onCancel={() => {
                            handleClose();
                        }}
                    />
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default AdminEvents;
